// SHORT REST / LONG REST, for the whole party at once.
//
// It writes to real D&D Beyond sheets across every character, so it says what
// it is about to do and waits to be told to do it. The preview is the feature:
// a DM who can see "Edam: 30 → 72 hit points · exhaustion 2 → 1 · Poisoned
// wore off" before clicking has a reason to trust the button the second time.
//
// Nothing here decides whether the party gets a rest. That is the DM's call and
// always was; this only spares them doing the arithmetic twelve times.
import { useState } from "react";
import { useStore } from "../state/store";
import { campaignRoster } from "../state/campaign";
import { restLabel, restParty, type RestKind } from "../state/rests";
import type { Character } from "../state/types";

export function RestButton() {
  const { layout, update, logActivity } = useStore();
  const [kind, setKind] = useState<RestKind | null>(null);
  const party = campaignRoster(layout);
  const preview = kind ? restParty(party, kind) : [];

  const take = () => {
    if (!kind) return;
    const byId = new Map(preview.map((r) => [r.character.id, r.changes]));
    update((l) => ({ ...l, characters: l.characters.map((c) => {
      const ch = byId.get(c.id);
      if (!ch) return c;
      /* undefined means "remove", which a spread would not do. */
      const next: Character = { ...c, ...ch };
      if ("concentrationOn" in ch && ch.concentrationOn === undefined) delete (next as Record<string, unknown>).concentrationOn;
      if ("light" in ch && ch.light === undefined) delete (next as Record<string, unknown>).light;
      return next;
    }) }));
    for (const r of preview) logActivity({ text: `${r.character.name}: ${restLabel(kind).toLowerCase()}`, meta: r.notes.join(" · "), tone: "green" });
    logActivity({ text: `${restLabel(kind)} — ${preview.length} character${preview.length === 1 ? "" : "s"} restored`, meta: preview.length ? "" : "nobody had anything to restore", tone: "teal" });
    setKind(null);
  };

  if (!kind) return (
    <span className="btn-group" role="group" aria-label="Rest">
      <button className="ghost small" title="Short rest: counters that recharge on one come back, temporary hit points and timed conditions end, concentration ends. Hit dice are the players' to spend." onClick={() => setKind("short")}>☾ Short rest</button>
      <button className="ghost small" title="Long rest: hit points to full, a drained maximum back, one level of exhaustion gone, every counter recharged, timed conditions and auras ended. A condition you set by hand is left alone." onClick={() => setKind("long")}>☾☾ Long rest</button>
    </span>
  );

  return (
    <div className="rest-preview" role="region" aria-label={`${restLabel(kind)} — what it restores`}>
      <strong>{restLabel(kind)}</strong>
      {preview.length === 0
        ? <em className="muted small">Nobody has anything to restore.</em>
        : <ul>{preview.map((r) => <li key={r.character.id}><b>{r.character.name}</b> <span className="muted small">{r.notes.join(" · ")}</span></li>)}</ul>}
      <span className="row">
        <button className="small primary" onClick={take} disabled={!preview.length}>Take the {restLabel(kind).toLowerCase()}</button>
        <button className="ghost small" onClick={() => setKind(null)}>Cancel</button>
      </span>
      <em className="muted small">Hit points, conditions and counters here are written to the D&amp;D Beyond sheets as any other change is.</em>
    </div>
  );
}
